import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpEvent,
} from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';
import { LoggerService } from './logger.service';

// Create a service to manage the refresh token state
class AuthInterceptorService {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private authService: AuthService) {}

  getIsRefreshing(): boolean {
    return this.isRefreshing;
  }

  setIsRefreshing(value: boolean): void {
    this.isRefreshing = value;
  }

  getRefreshTokenSubject(): BehaviorSubject<any> {
    return this.refreshTokenSubject;
  }

  resetRefreshTokenSubject(): void {
    this.refreshTokenSubject.next(null);
  }

  setRefreshTokenSubject(token: any): void {
    this.refreshTokenSubject.next(token);
  }

  /** Releases requests waiting for the refresh, so they fail instead of hanging forever. */
  failRefresh(): void {
    this.refreshTokenSubject.next(false);
  }
}

// Create a token for the service
export const AUTH_INTERCEPTOR_SERVICE = new InjectionToken<AuthInterceptorService>(
  'AuthInterceptorService',
);

// Factory function to create the service
export function createAuthInterceptorService(): AuthInterceptorService {
  const authService = inject(AuthService);
  return new AuthInterceptorService(authService);
}

// Functional interceptor
export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const interceptorService = inject(AUTH_INTERCEPTOR_SERVICE);
  const logger = inject(LoggerService);

  // Don't add token to auth endpoints and public storefront endpoints
  if (
    request.url.includes('/auth/login') ||
    // endsWith: /auth/google/link and /auth/google/unlink are authenticated
    request.url.endsWith('/auth/google') ||
    request.url.includes('/auth/refresh') ||
    request.url.includes('/auth/welcome') ||
    request.url.includes('/auth/register') ||
    request.url.includes('/companies/by-suburl/') ||
    request.url.includes('/shop-drafts/previews/') ||
    request.url.endsWith('/shop-drafts/claim-info') ||
    // The partner area has its own session (partnerAuthInterceptor)
    request.url.includes('/partner/')
  ) {
    logger.log('🌐 [AuthInterceptor] Skipping auth for public endpoint:', request.url);
    return next(request);
  }

  const tokenStorage = inject(TokenStorageService);
  const token = tokenStorage.getAccessToken();
  if (token) {
    logger.log('🔐 [AuthInterceptor] Adding auth token to request:', request.url);
    request = addToken(request, token);
  } else {
    logger.log('🔓 [AuthInterceptor] No token available for request:', request.url);
  }

  // Endpoints where 401 means "wrong credentials", not "token expired" — never refresh/logout on these.
  // /auth/logout belongs here too: it runs after a failed refresh, and refreshing on its 401
  // would trigger yet another logout.
  const credentialEndpoints = ['/auth/change-email', '/auth/change-password', '/auth/logout'];
  const isCredentialEndpoint = credentialEndpoints.some((ep) => request.url.includes(ep));

  return next(request).pipe(
    catchError((error) => {
      // For credential-mutation endpoints, pass the error straight through so the component can show it
      if (isCredentialEndpoint) {
        return throwError(() => error);
      }

      // Handle both 401 and CORS errors (status 0)
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 0)) {
        logger.log('🔄 [AuthInterceptor] Caught error, attempting refresh:', {
          status: error.status,
          url: request.url,
        });
        if (!interceptorService.getIsRefreshing()) {
          interceptorService.setIsRefreshing(true);
          interceptorService.resetRefreshTokenSubject();

          // catchError sits before switchMap so it only sees a failed refresh. A retried
          // request that still gets a 401 (the token is fine, the resource is not ours)
          // is passed to the caller and must not log the user out.
          return authService.refreshToken().pipe(
            catchError((refreshError) => {
              logger.error('❌ [AuthInterceptor] Token refresh failed:', {
                status: refreshError.status,
                message: refreshError.message,
                url: request.url,
              });
              interceptorService.setIsRefreshing(false);
              interceptorService.failRefresh();
              // Only a 401 means the session is gone. A refresh that failed because the
              // backend was unreachable (laptop just woke up, deploy) keeps the session;
              // the request fails and the next one tries again.
              if (refreshError.status === 401) {
                authService.logout();
              }
              return throwError(() => refreshError);
            }),
            switchMap((response) => {
              logger.log(
                '✅ [AuthInterceptor] Token refresh successful, retrying request:',
                request.url,
              );
              interceptorService.setIsRefreshing(false);
              interceptorService.setRefreshTokenSubject(response.access_token);
              return next(addToken(request, response.access_token));
            }),
          );
        } else {
          // Wait for the refresh to complete and retry the request
          return interceptorService.getRefreshTokenSubject().pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) =>
              token ? next(addToken(request, token)) : throwError(() => error),
            ),
          );
        }
      }
      return throwError(() => error);
    }),
  );
};

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
    withCredentials: true, // Required for cross-origin in production (app.schenkwerk.com -> api.schenkwerk.com)
  });
}
